import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Breadcrumb, Button, Dimmer, Loader, Modal, Tab } from 'semantic-ui-react'
import { EditableSimpleTable } from 'components/SimpleTable'
import clientActions from '../../store/actions/clients'
import { connect } from 'react-redux'
import Swal from 'sweetalert2'
import ClientContactWebPermissions from './ClientContactWebPermissions'
import _, { set } from 'lodash'
import ClientContactGeneralPermissions from './ClientContactGeneralPermissions'


const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

const ClientUsers = (props) => {
    const { t } = useTranslation()
    const [isLoading, setIsLoading] = useState(true);
    const [clientUsersList, setClientUsersList] = useState([]);
    const [clientPassword, setClientPassword] = useState({});
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [selectedClientContact, setSelectedClientContact] = useState(null);
    const [clientContactPermits, setClientContactPermits] = useState({})
    const [allClientRoutes, setAllClientRoutes] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [groupPassengers, setGroupPassengers] = useState([])
    const [isSavingPermits, setIsSavingPermits] = useState(false)
    const [localRows, setLocalRows] = useState([])
    const [originalRows, setOriginalRows] = useState([])
    const [hasChanges, setHasChanges] = useState(false)
    const [deletedIds, setDeletedIds] = useState([])
    const [searchText, setSearchText] = useState('')
    const [filterValues, setFilterValues] = useState({})
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 30

    const sections = [
        { key: 'users', content: t('Usuarios'), active: true },
    ]

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [users, clientModules, groupPassengers] = await Promise.all([
                    props.getClientUsers(),
                    props.getClientModules(false),
                    props.getGroupPassengersByClient()
                ]);
                setClientUsersList(_.orderBy(users, ['deletedAt', 'id'], ['desc', 'asc']));
                setAvailablePermissions(clientModules);
                setAllClientRoutes(_.map(clientModules, (item) =>
                    _.map(item[Object.keys(item)[0]].routes, (route) => route.url)
                ).flat());
                setGroupPassengers(groupPassengers);
            } catch (error) {
                console.error('Error fetching client users:', error);
            } finally {
                setIsLoading(false);
            }
        }

        fetchData();
    }, []);

    useEffect(() => {
        setClientPassword(clientUsersList.reduce((acc, user) => {
            acc[user.id] = user.password;
            return acc;
        }, {}));

        setClientContactPermits(clientUsersList.reduce((acc, user) => {
            acc[user.id] = user.Permits;
            return acc;
        }, {}));
    }, [clientUsersList])

    useEffect(() => {
        setLocalRows(_.orderBy(clientUsersList, ['deletedAt', 'id'], ['desc', 'asc']))
        setOriginalRows(_.orderBy(clientUsersList, ['deletedAt', 'id'], ['desc', 'asc']))
        setHasChanges(false)
        setDeletedIds([])
    }, [clientUsersList])

    const handleCellChange = useCallback((idx, col, val) => {
        setLocalRows(prev => {
            const row = prev[idx]
            if (row.deletedAt) return prev
            return prev.map((r, i) => i === idx ? { ...r, [col]: val } : r)
        })
        setHasChanges(true)
    }, [])

    const handleAddRow = useCallback(() => {
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
        setClientContactPermits(prev => ({
            ...prev,
            [tempId]: {
                web_permissions: [...allClientRoutes],
                form_permits: false,
                chat_passenger_permits: false,
            }
        }))
        setLocalRows(prev => [{
            _temp: true,
            id: tempId,
            user_active: true,
            account_type: 'USER',
            form_active: false,
            invoice_active: false,
            password: '',
            repeat_password: '',
            name: '',
            email: '',
            default_group_id: null,
        }, ...prev])
        setHasChanges(true)
    }, [allClientRoutes])

    const handleDelete = useCallback((row) => {
        const idx = localRows.findIndex(r => r.id === row.id)
        if (idx === -1) return
        setLocalRows(prev => prev.map((r, i) => i === idx ? { ...r, deletedAt: new Date().toISOString() } : r))
        if (row.id && !row._temp) setDeletedIds(d => [...d, row.id])
        setHasChanges(true)
    }, [localRows])

    const handleRestore = useCallback((row) => {
        const idx = localRows.findIndex(r => r.id === row.id)
        if (idx === -1) return
        setLocalRows(prev => prev.map((r, i) => i === idx ? { ...r, deletedAt: null } : r))
        setHasChanges(true)
    }, [localRows])

    const displayData = useMemo(() =>
        _.orderBy(localRows, ['deletedAt', 'id'], ['desc', 'asc']).map(r => ({
            ...r,
            email: r.email ? r.email.toLowerCase() : '',
            displayDeleted: r.deletedAt ? t('Sí') : t('No'),
        }))
    , [localRows, t])

    const groupPassengersOptions = useMemo(() => [{
        key: 'none', value: null, text: t('Ninguno')
    }].concat(groupPassengers.map(gp => ({ key: gp.id, value: gp.id, text: gp.name }))), [groupPassengers, t]);

    const filteredData = useMemo(() => {
        let result = displayData
        if (searchText) {
            const q = searchText.toLowerCase()
            result = result.filter(row =>
                Object.values(row).some(v => String(v ?? '').toLowerCase().includes(q))
            )
        }
        if (Object.keys(filterValues).length) {
            result = result.filter(row =>
                Object.entries(filterValues).every(([key, values]) =>
                    !values?.length || values.some(v => {
                        const rv = row[key]
                        if (v === null || v === '') return rv == null || rv === ''
                        return String(rv ?? '') === String(v ?? '')
                    })
                )
            )
        }
        return result
    }, [displayData, searchText, filterValues])

    const paginatedData = useMemo(() =>
        filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredData, page])

    const handlePageChange = useCallback((p) => setPage(p), [])
    const handleFilterChange = useCallback((accessor, values) => {
        setFilterValues(prev => {
            if (!accessor) return {}
            if (values === null) { const n = { ...prev }; delete n[accessor]; return n }
            return { ...prev, [accessor]: values }
        })
        setPage(1)
    }, [])
    const handleSearchChange = useCallback((v) => { setSearchText(v); setPage(1) }, [])

    const handleSave = async () => {
        try {
            const data = {
                createItems: localRows.filter(r => r._temp).map(r => ({
                    id: r.id,
                    user_active: r.user_active,
                    name: r.name,
                    email: r.email,
                    password: r.password,
                    repeat_password: r.repeat_password,
                    account_type: r.account_type,
                    department: r.department,
                    job_title: r.job_title,
                    phone: r.phone,
                    nip: r.nip,
                    default_group_id: r.default_group_id,
                    form_active: r.form_active,
                    invoice_active: r.invoice_active,
                })),
                updateItems: localRows.filter(r => !r._temp && !r.deletedAt).map(r => ({
                    id: r.id,
                    user_active: r.user_active,
                    name: r.name,
                    email: r.email,
                    password: r.password || clientPassword[r.id],
                    repeat_password: r.repeat_password,
                    account_type: r.account_type,
                    department: r.department,
                    job_title: r.job_title,
                    phone: r.phone,
                    nip: r.nip,
                    default_group_id: r.default_group_id,
                    form_active: r.form_active,
                    invoice_active: r.invoice_active,
                    deletedAt: r.deletedAt,
                })),
                deleteItems: deletedIds.map(id => ({ id })),
                rows: localRows,
            }

            if (!validateUserData(data)) {
                setHasChanges(true)
                return
            }

            setIsSaving(true)
            const savedClientUsersList = await props.saveClientUsers({ ...data, clientContactPermits })
            setClientUsersList(_.orderBy(savedClientUsersList, ['deletedAt', 'id'], ['desc', 'asc']))

            Swal.fire({
                icon: 'success',
                title: t('¡Éxito!'),
                text: t('Los datos se han guardado correctamente.'),
                showCloseButton: true,
            })
            setHasChanges(false)

            const isUserInactive = (isActive) => {
                if (!isActive || isActive === 'false') return true
                return false
            }

            if (
                (data.updateItems.some(i => i.id === props.client.id) && (
                    data.updateItems.find(i => i.id === props.client.id).account_type !== 'ADMIN'
                    || isUserInactive(data.updateItems.find(i => i.id === props.client.id).user_active)
                )) || data.deleteItems.some(i => i.id === props.client.id)
            ) {
                window.location.reload()
            }
        } catch (error) {
            if ('duplicateEmails' in error) {
                Swal.fire({ icon: 'error', title: 'Error de validación', html: `<div style="text-align:left; line-height:1.5; font-size:14px;"><p style="margin:0 0 8px;">${t('Los siguientes <strong>correos electrónicos ya están en uso</strong>:')}</p><div style="max-height:150px; overflow-y:auto; margin:8px 0 12px; border:1px solid #eee; border-radius:6px; padding:6px 10px; background:#fafafa;"><ul style="margin:0; padding-left:18px; list-style:disc;">${error.duplicateEmails.map(email => `<li style="margin:4px 0;"><code style="background:#f4f4f5; padding:2px 6px; border-radius:6px; display:inline-block;">${email}</code></li>`).join('')}</ul></div><div style="margin-top:10px; padding:10px 12px; background:#fff3cd; border:1px solid #ffeeba; border-radius:8px; color:#664d03;"><strong>${t('Importante:')}</strong>${t('Use otra dirección de correo o verifique que no exista ya en el sistema antes de continuar.')}</div></div>`, confirmButtonText: t('Entendido'), showCloseButton: true, width: 640 })
            } else if ('error' in error) {
                Swal.fire({ icon: 'error', title: t('Error'), text: error.error, showCloseButton: true })
            } else {
                Swal.fire({ title: t('¡Advertencia!'), text: t('Ha ocurrido un error. Favor de contactar el administrador del sistema.'), icon: 'error', showCancelButton: true, cancelButtonText: t('Cerrar'), showCloseButton: true })
            }
            setHasChanges(true)
        } finally {
            setIsSaving(false)
        }
    }

    const handleCancel = () => {
        setLocalRows(originalRows)
        setClientContactPermits(clientUsersList.reduce((acc, user) => {
            acc[user.id] = user.Permits
            return acc
        }, {}))
        setHasChanges(false)
        setDeletedIds([])
    }

    const handleRefresh = () => {
        setIsLoading(true)
        const fetchData = async () => {
            try {
                const [users, clientModules, groupPassengers] = await Promise.all([
                    props.getClientUsers(),
                    props.getClientModules(false),
                    props.getGroupPassengersByClient()
                ])
                setClientUsersList(_.orderBy(users, ['deletedAt', 'id'], ['desc', 'asc']))
                setAvailablePermissions(clientModules)
                setAllClientRoutes(_.map(clientModules, (item) =>
                    _.map(item[Object.keys(item)[0]].routes, (route) => route.url)
                ).flat())
                setGroupPassengers(groupPassengers)
            } catch (error) {
                console.error('Error fetching client users:', error)
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }

    const validateUserData = (data) => {
        if (data.rows.some(cc => !cc.email || !cc.password || !cc.name)) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: t('Todos los usuarios deben tener nombre, correo electrónico y contraseña.'),
                showCloseButton: true,
            });
            return false;
        }

        //? Todos los correos deben tener formato válido
        const emails = data.rows.map(row => row.email.toLowerCase().trim());
        const emailSet = [...new Set(emails)];
        const invalidEmails = emailSet.filter(email => !isEmailValid(email));

        if (invalidEmails.length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'Error de validación',
                html: `
                        <div style="text-align:left; line-height:1.5; font-size:14px;">
                        <p style="margin:0 0 8px;">
                            ${t('Los siguientes <strong>emails no son válidos</strong>:')}
                        </p>

                        <div style="max-height:150px; overflow-y:auto; margin:8px 0 12px; border:1px solid #eee; border-radius:6px; padding:6px 10px; background:#fafafa;">
                            <ul style="margin:0; padding-left:18px; list-style:disc;">
                            ${invalidEmails.map(email => `
                                <li style="margin:4px 0;">
                                <code style="background:#f4f4f5; padding:2px 6px; border-radius:6px; display:inline-block;">${email}</code>
                                </li>
                            `).join('')}
                            </ul>
                        </div>

                        <div style="margin-top:10px; padding:10px 12px; background:#f8d7da; border:1px solid #f5c2c7; border-radius:8px; color:#842029;">
                            <strong>${t('Importante:')}</strong>
                            ${t('Verifique que las direcciones de correo estén bien escritas y tengan un formato válido (por ejemplo: <em>usuario@dominio.com</em>).')}
                        </div>
                        </div>
                    `,
                confirmButtonText: t('Entendido'),
                showCloseButton: true,
                width: 640
            });

            return false;
        }

        //? No se pueden repetir emails entre los usuarios
        if (emailSet.some(email => emails.filter(e => e === email).length > 1)) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: t('No se pueden repetir emails entre los usuarios.'),
                showCloseButton: true,
            });
            return false;
        }

        //? Las contraseñas deben coincidir si se están creando nuevos usuarios o actualizando existentes
        const mismatchedPasswordEmails = [];
        data.createItems.forEach(item => {
            if (item.password !== item.repeat_password) {
                mismatchedPasswordEmails.push(item.email);
            }
        });

        data.updateItems.forEach(item => {
            if (clientPassword[item.id] !== item.password && item.password !== item.repeat_password) {
                mismatchedPasswordEmails.push(item.email);
            }
        });

        if (mismatchedPasswordEmails.length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'Error de validación',
                html: `
                        <div style="text-align:left; line-height:1.5; font-size:14px;">
                        <p style="margin:0 0 8px;">
                            ${t('La columna <strong>"Repetir contraseña"</strong> <span style="color:#d33;font-weight:700;">no coincide</span> con la columna <strong>"Contraseña"</strong> en los siguientes correos:')}
                        </p>

                        <div style="max-height:150px; overflow-y:auto; margin:8px 0 12px; border:1px solid #eee; border-radius:6px; padding:6px 10px; background:#fafafa;">
                            <ul style="margin:0; padding-left:18px; list-style:disc;">
                            ${mismatchedPasswordEmails.map(email => `
                                <li style="margin:4px 0;">
                                <code style="background:#f4f4f5; padding:2px 6px; border-radius:6px; display:inline-block;">${email}</code>
                                </li>
                            `).join('')}
                            </ul>
                        </div>

                        <div style="margin-top:10px; padding:10px 12px; background:#fff3cd; border:1px solid #ffeeba; border-radius:8px;">
                            <strong>${t('Importante:')}</strong>
                            ${t('si está <em>creando un usuario nuevo</em> o <em>editando la contraseña</em> de un usuario existente, debe rellenar <strong>"Repetir contraseña"</strong> con la misma contraseña que escribió en <strong>"Contraseña"</strong>.')}
                        </div>
                        </div>
                    `,
                confirmButtonText: t('Entendido'),
                showCloseButton: true,
                width: 640
            });


            return false;
        }

        return true;
    }

    const handleClose = () => setSelectedClientContact(null)

    const saveClientUserPermits = async (updatedPermissions) => {
        setIsSavingPermits(true)
        setClientContactPermits(prev => ({
            ...prev,
            [selectedClientContact.id]: updatedPermissions
        }))
        setHasChanges(true)
        await Promise.resolve()
        setIsSavingPermits(false)
        handleClose()
    }

    const panes = [
        {
            menuItem: 'Web',
            render: () => (
                <ClientContactWebPermissions
                    availableModules={availablePermissions}
                    currentPermissions={clientContactPermits[selectedClientContact.id]?.web_permissions}
                    onChange={(permissions) => {
                        setClientContactPermits(prev => ({
                            ...prev,
                            [selectedClientContact.id]: { web_permissions: permissions }
                        }))
                    }}
                />
            ),
        },
        {
            menuItem: t('General'),
            render: () => (
                <ClientContactGeneralPermissions
                    permissions={clientContactPermits[selectedClientContact.id]}
                    onChange={(permissions) => {
                        setClientContactPermits(prev => ({
                            ...prev,
                            [selectedClientContact.id]: permissions
                        }))
                    }}
                />
            ),
        }
    ]

    return (
        <div style={{ margin: 20 }}>
            <Breadcrumb icon='angle right' sections={sections} />

            <EditableSimpleTable
                tableId="client-users-main"
                // title={t("Usuarios del Cliente")}
                columns={[
                    { key: 'user_active', Header: t('Activo'), accessor: 'user_active', type: 'boolean', editable: true, width: 100 },
                    { Header: t('Nombre') + ' *', accessor: 'name', editable: true, width: 250, filterable: true, sortable: true },
                    { Header: t('Correo electrónico') + ' *', accessor: 'email', editable: true, width: 250, filterable: true, sortable: true },
                    { Header: t('Contraseña') + ' *', accessor: 'password', editable: true, width: 200, type: 'password' },
                    { Header: t('Repetir contraseña'), accessor: 'repeat_password', editable: true, width: 200, type: 'password' },
                    { key: 'account_type', Header: t('Tipo de cuenta'), accessor: 'account_type', type: 'select', editable: true, width: 175, options: [
                        { key: 'ADMIN', value: 'ADMIN', text: 'Admin' },
                        { key: 'USER', value: 'USER', text: t('Usuario') }
                    ]},
                    { Header: t('Departamento'), accessor: 'department', editable: true, width: 175, filterable: true, sortable: true },
                    { Header: t('Puesto'), accessor: 'job_title', editable: true, width: 175, filterable: true, sortable: true },
                    { Header: t('Teléfono'), accessor: 'phone', editable: true, width: 175, filterable: true, sortable: true },
                    { Header: t('NIP'), accessor: 'nip', editable: true, width: 100, filterable: true, sortable: true },
                    { key: 'default_group_id', Header: t('Grupo de Pasajeros Predeterminado'), accessor: 'default_group_id', type: 'select', editable: true, width: 300, options: groupPassengersOptions },
                    { key: 'form_active', Header: t('Formulario'), accessor: 'form_active', type: 'boolean', editable: true, width: 150 },
                    { key: 'invoice_active', Header: t('Facturación'), accessor: 'invoice_active', type: 'boolean', editable: true, width: 150 },
                    { Header: t('Eliminado'), accessor: 'displayDeleted', width: 160 },
                ]}
                rows={paginatedData}
                filterValues={filterValues}
                onFilterChange={handleFilterChange}
                loading={isLoading}
                onCellChange={handleCellChange}
                onAddRow={handleAddRow}
                onDelete={(row) => handleDelete(row)}
                onSave={handleSave}
                onCancel={handleCancel}
                isDirty={hasChanges}
                isSaving={isSaving}
                searchValue={searchText}
                onSearchChange={handleSearchChange}
                searchPlaceholder={t('Buscar usuarios...')}
                onRefresh={handleRefresh}
                customActions={(row) => (
                    <>
                        {!row.deletedAt && !row._temp && (
                            <Button icon="lock" size="mini" color="teal" basic
                                onClick={(e) => { e.stopPropagation(); setSelectedClientContact(row) }}
                                style={{ padding: 1, minWidth: 20, minHeight: 18, fontSize: 9, margin: '0 1px' }}
                            />
                        )}
                        {row.deletedAt && !row._temp && (
                            <Button icon="undo" size="mini" color="green" basic
                                onClick={(e) => { e.stopPropagation(); handleRestore(row) }}
                                style={{ padding: 1, minWidth: 20, minHeight: 18, fontSize: 9, margin: '0 1px' }}
                            />
                        )}
                    </>
                )}
            />



            {selectedClientContact && (
                <Modal open={!!selectedClientContact} onClose={handleClose} size='small'>
                    <Modal.Header>{t('Permisos de la aplicación')}</Modal.Header>
                    <Modal.Content scrolling style={{ paddingTop: 0 }}>
                        <Tab
                            menu={{ secondary: true, pointing: true }}
                            panes={panes}
                        />
                    </Modal.Content>
                    <Modal.Actions>
                        <Button type='button' onClick={handleClose} disabled={isSavingPermits}>{t('Cancelar')}</Button>
                        <Button
                            type='button'
                            onClick={() => {
                                saveClientUserPermits(clientContactPermits[selectedClientContact.id])
                            }}
                            positive
                            disabled={isSavingPermits}
                            loading={isSavingPermits}
                        >
                            {t('Guardar')}
                        </Button>
                    </Modal.Actions>
                </Modal>
            )}

            <Dimmer active={isSaving}>
                <Loader>{t('Guardando...')}</Loader>
            </Dimmer>
        </div>
    )
}

const mapStateToProps = (state) => ({
    client: state.app.client,
})

const mapDispatchToProps = (dispatch) => ({
    getClientUsers: () => dispatch(clientActions.getClientUsers()),
    saveClientUsers: (data) => dispatch(clientActions.saveClientUsers(data)),
    getClientModules: (shouldIncludeAdminModules) => dispatch(clientActions.getClientModules(shouldIncludeAdminModules)),
    getGroupPassengersByClient: () => dispatch(clientActions.getGroupPassengersByClient()),
})

export default connect(mapStateToProps, mapDispatchToProps)(ClientUsers)

const getRowHighlightColor = (row) => !!row?.isMarkedForRestoration ?
    '#d4f4dd'
    : row.deletedAt ? '#f5b7b1' : 'white'
